<script setup lang="ts">
import { centsToDollars } from '@/services/currency'
import { useReceiptQuery } from '@/features/tickets/api/queries'
import BKButton from '@/components/AppShared/BKButton.vue'

const props = defineProps<{
  purchaseId: number
  hideActions?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { data: receipt, isLoading, error, refetch } = useReceiptQuery(props.purchaseId)
</script>

<template>
  <div class="purchase-receipt">
    <template v-if="receipt && !isLoading && !error">
      <div class="receipt-content">
        <div class="receipt-header">
          <h3 class="receipt-title">Receipt</h3>
          <div
            v-if="receipt.purchasedAt"
            class="receipt-date"
          >
            <p class="date-text">Purchased on</p>
            <p class="date-value">
              {{
                new Date(receipt.purchasedAt).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                  timeZoneName: 'short',
                })
              }}
            </p>
          </div>
        </div>

        <h4 class="receipt-items-title">Order summary</h4>

        <div class="receipt-items">
          <div
            v-for="tier in receipt.tiers"
            :key="tier.tierId"
            class="receipt-item"
          >
            <span class="item-title">{{ tier.title }}</span>
            <span class="item-quantity"> {{ tier.quantity }}x{{ centsToDollars(tier.price) }} </span>
            <span class="item-subtotal">{{ centsToDollars(tier.subtotal) }}</span>
          </div>
        </div>

        <div class="receipt-summary">
          <div class="summary-line">
            <span class="summary-label">Tickets:</span>
            <span class="summary-value">{{ centsToDollars(receipt.subtotal) }}</span>
          </div>
          <div class="summary-line">
            <span class="summary-label">Platform Fee:</span>
            <span class="summary-value">{{ centsToDollars(receipt.platformFee) }}</span>
          </div>
          <div class="summary-line">
            <span class="summary-label">Taxes:</span>
            <span class="summary-value">{{ centsToDollars(receipt.taxes) }}</span>
          </div>
          <div class="summary-line total-line">
            <span class="summary-label">Total:</span>
            <span class="summary-value">{{ centsToDollars(receipt.total) }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="!hideActions"
        class="actions"
      >
        <BKButton
          class="action-button"
          outlined
          @click="emit('close')"
        >
          Back
        </BKButton>
      </div>
    </template>

    <div
      v-else-if="error"
      class="error-state"
    >
      <h3 class="error-title">Failed to Load Receipt</h3>
      <p class="error-message">We couldn't load your receipt at this time. Please try again.</p>
      <BKButton
        outlined
        width="auto"
        @click="() => refetch()"
      >
        Try Again
      </BKButton>
    </div>

    <div
      v-else-if="isLoading"
      class="loading-state"
    >
      <div class="loading-spinner">
        <div class="spinner"></div>
      </div>
      <p class="loading-text">Loading receipt...</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.purchase-receipt {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  p {
    margin: 0;
  }
}

.receipt-content {
  display: flex;
  flex-direction: column;
}

.receipt-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;

  .receipt-title {
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    color: #000;
  }

  .receipt-date {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .date-text {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      color: #333;
    }

    .date-value {
      font-size: 16px;
      line-height: 24px;
      color: #333;
    }
  }
}

.receipt-items-title {
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #333;
  margin-bottom: 8px;
}

.receipt-items {
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px dashed #eee;
}

.receipt-item {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  font-size: 16px;
  line-height: 24px;
  color: #333;

  .item-title,
  .item-subtotal {
    font-weight: 500;
  }

  .item-quantity,
  .item-subtotal {
    text-align: right;
  }
}

.receipt-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .summary-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    line-height: 24px;
    color: #333;

    &.total-line {
      padding-top: 8px;
      border-top: 1px dashed #eee;
      font-weight: 500;
    }
  }
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;

  .action-button {
    flex: 1 1 0;
    min-width: fit-content;
  }
}

.loading-state {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .loading-spinner {
    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #f0f0f0;
      border-top: 3px solid #007bff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  .loading-text {
    margin: 0;
    color: #666;
    font-size: 16px;
  }
}

.error-state {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .error-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: var(--color-dark-800);
  }

  .error-message {
    margin: 0;
    color: var(--color-grey-300);
    font-size: 16px;
    line-height: 1.5;
    max-width: 300px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
